
import React, { useState, Suspense } from "react"
import { toast } from "react-toastify"
import { Routes, Route, useNavigate } from "react-router-dom"
import ProjectsTable from "../components/ProjectsTable"
// Lazy-load form dialog to avoid hook errors at initial render
const ProjectFormDialog = React.lazy(() => import("../components/ProjectFormDialog"))
import DeleteProjectAlert from "../components/DeleteProjectAlert"
import ProjectImportWizard from "../wizard/ProjectImportWizard"
import ProjectDetailPage from "./ProjectDetailPage"

import { useProjects, useCreateProject, useDeleteProject } from "../hooks/useProjects"
import { useAvailableSeats } from "../hooks/useSeats"
import { useProjectMeta } from "../hooks/useProjectMeta"
import type { Project, ProjectFormValues } from "../hooks/types"

function ProjectsPageContent() {
  const navigate = useNavigate()

  // State for dialogs
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false)
  const [isImportWizardOpen, setIsImportWizardOpen] = useState(false)
  const [projectToDelete, setProjectToDelete] = useState<Project | null>(null)

  // Queries and mutations
  const projectsQuery = useProjects()
  const projectMetaQuery = useProjectMeta()
  const createProject = useCreateProject()
  const deleteProject = useDeleteProject()

  // Available seats for prerequisite check
  const availablePMSeats = useAvailableSeats("Project Manager")
  const availableForemanSeats = useAvailableSeats("Foreman")
  // Handle create project with guard
  const handleCreateProject = async (data: ProjectFormValues) => {
    try {
      await createProject.mutateAsync(data)
      setIsCreateDialogOpen(false)
    } catch (error) {
      console.error("Failed to create project:", error)
    }
  }

  // Handle delete project
  const handleDeleteProject = async () => {
    if (!projectToDelete) return

    try {
      await deleteProject.mutateAsync(projectToDelete._id)
      setProjectToDelete(null)
    } catch (error) {
      console.error("Failed to delete project:", error)
    }
  }

  // Handle import success
  const handleImportSuccess = () => {
    setIsImportWizardOpen(false)
    projectsQuery.refetch()
  }

  return (
    <div className="container mx-auto py-8 px-4">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Projects</h1>
        <div className="flex space-x-2">
          <button
            onClick={() => {
              // Ensure at least one free PM and one free Foreman seat
              const pmCount = availablePMSeats.data?.length || 0
              const foremanCount = availableForemanSeats.data?.length || 0
              if (pmCount < 1 || foremanCount < 1) {
                toast.error(
                  "You need at least two free seats (1 PM, 1 Foreman) before starting a project. Please add seats under Organisation → Seats."
                )
                return
              }
              setIsCreateDialogOpen(true)
            }}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 flex items-center"
          >
            <svg
              className="w-4 h-4 mr-2"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
            </svg>
            New Project
          </button>
          <button
            onClick={() => setIsImportWizardOpen(true)}
            className="px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded hover:bg-gray-50 flex items-center"
          >
            <svg
              className="w-4 h-4 mr-2"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
              />
            </svg>
            Import Excel
          </button>
        </div>
      </div>

      {projectsQuery.isLoading ? (
        <div className="flex justify-center py-12">
          <svg className="h-8 w-8 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            ></path>
          </svg>
        </div>
      ) : projectsQuery.isError ? (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          Failed to load projects. Please try again.
        </div>
      ) : (
        <ProjectsTable
          projects={projectsQuery.data || []}
          isLoading={projectsQuery.isLoading}
          onEdit={(project) => navigate(`/myteconstruction/dashboard/projects/${project._id}`)}
          onDelete={(project) => setProjectToDelete(project)}
        />
      )}

      {/* Create Project Dialog */}
      {isCreateDialogOpen && (
        <Suspense fallback={null}>
          <ProjectFormDialog
            isOpen={isCreateDialogOpen}
            onClose={() => setIsCreateDialogOpen(false)}
            onSubmit={handleCreateProject}
            isLoading={createProject.isPending}
            projectMeta={projectMetaQuery.data}
            isMetaLoading={projectMetaQuery.isLoading}
            mode="create"
          />
        </Suspense>
      )}

      {/* Delete Project Alert */}
      <DeleteProjectAlert
        isOpen={!!projectToDelete}
        onClose={() => setProjectToDelete(null)}
        onConfirm={handleDeleteProject}
        isLoading={deleteProject.isPending}
        projectTitle={projectToDelete?.title}
      />

      {/* Import Wizard */}
      <ProjectImportWizard
        isOpen={isImportWizardOpen}
        onClose={() => setIsImportWizardOpen(false)}
        onSuccess={handleImportSuccess}
      />
    </div>
  )
}

export default function ProjectsPage() {
  return (
    <Routes>
      <Route path="/" element={<ProjectsPageContent />} />
      <Route path="/:id" element={<ProjectDetailPage />} />
    </Routes>
  )
}
